// Pinch / drag / wheel / double-tap zoom-and-pan logic for the public live
// view. Hand-rolled (no `react-zoom-pan-pinch` dep) because the live bundle
// is the size-sensitive one — the whole hook is < 2 kB gzipped.
//
// Returns the current transform values + a `handlers` object you spread onto
// the container element. The transform is applied as
//   `translate(tx px, ty px) scale(s)`
// with `transform-origin: 0 0` so anchor math is straightforward.
//
// Coordinate space: tx / ty are in container-local pixels. At scale === 1
// they're hard-pinned to (0, 0) so the image never walks off-screen when the
// guest drags without zooming first.

import { useCallback, useEffect, useRef, useState } from "react";

export interface PinchPanState {
  scale: number;
  tx: number;
  ty: number;
}

export interface PinchPanHandlers {
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerCancel: (e: React.PointerEvent) => void;
  onWheel: (e: React.WheelEvent) => void;
  onDoubleClick: (e: React.MouseEvent) => void;
}

export interface UsePinchPanResult extends PinchPanState {
  handlers: PinchPanHandlers;
  reset: () => void;
  zoomBy: (factor: number, anchor?: { x: number; y: number }) => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 5;
// Per-notch wheel zoom factor. 1.15 = ~7 wheel notches across the full range.
const WHEEL_STEP = 1.15;

interface PointerRecord {
  x: number;
  y: number;
}

export function usePinchPan(
  containerW: number,
  containerH: number
): UsePinchPanResult {
  const [state, setState] = useState<PinchPanState>({ scale: 1, tx: 0, ty: 0 });

  // We keep the live pointer set + last pinch distance in refs so handler
  // closures stay stable; React state only flips at the end of each gesture
  // step (cheap because transform is the only DOM property that changes).
  const pointers = useRef<Map<number, PointerRecord>>(new Map());
  const lastPinchDist = useRef<number | null>(null);
  const lastPinchMid = useRef<{ x: number; y: number } | null>(null);
  // Mirror of `state.scale` accessible synchronously inside pointer handlers
  // (which use closures that would otherwise see a stale scale). Updated
  // immediately after each render via the effect below.
  const scaleRef = useRef(1);
  useEffect(() => {
    scaleRef.current = state.scale;
  }, [state.scale]);

  // Pin (tx, ty) into the legal range so the image can never be panned off
  // the container at the current scale. At scale === 1 the only legal value
  // is (0, 0).
  const clamp = useCallback(
    (s: number, x: number, y: number): { tx: number; ty: number } => {
      if (s <= 1) return { tx: 0, ty: 0 };
      const maxX = 0;
      const minX = containerW - containerW * s;
      const maxY = 0;
      const minY = containerH - containerH * s;
      return {
        tx: Math.min(maxX, Math.max(minX, x)),
        ty: Math.min(maxY, Math.max(minY, y)),
      };
    },
    [containerW, containerH]
  );

  const reset = useCallback(() => {
    setState({ scale: 1, tx: 0, ty: 0 });
  }, []);

  const zoomBy = useCallback(
    (factor: number, anchor?: { x: number; y: number }) => {
      const a = anchor ?? { x: containerW / 2, y: containerH / 2 };
      setState((prev) => {
        const target = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev.scale * factor));
        if (target === prev.scale) return prev;
        const worldX = (a.x - prev.tx) / prev.scale;
        const worldY = (a.y - prev.ty) / prev.scale;
        const nextTx = a.x - worldX * target;
        const nextTy = a.y - worldY * target;
        const c = clamp(target, nextTx, nextTy);
        return { scale: target, tx: c.tx, ty: c.ty };
      });
    },
    [clamp, containerW, containerH]
  );

  // ---- pointer handlers ----
  const onPointerDown = useCallback((e: React.PointerEvent): void => {
    const target = e.currentTarget as Element;
    const rect = target.getBoundingClientRect();
    pointers.current.set(e.pointerId, {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    // Conditional pointer capture — critical for the operator surface:
    //   • Pinch starting (2+ pointers down): capture both so the gesture
    //     stays bound to this element even if fingers drift off-edge.
    //   • Single-finger pan at scale > 1: capture so the finger can drag
    //     off-edge without losing tracking.
    //   • Single finger at scale === 1 (tap-like): DO NOT capture, so the
    //     synthesised click fires on the underlying SVG zone instead of
    //     being absorbed by this viewport. This is what makes operator
    //     taps work after wrapping ZoneOverlay in ZoomPanContainer.
    if (pointers.current.size >= 2) {
      target.setPointerCapture?.(e.pointerId);
      // The previously-down pointer was deliberately not captured (it
      // arrived when there was only one finger at scale=1). Capture it
      // now so move/up events for it route here too.
      for (const id of pointers.current.keys()) {
        if (id !== e.pointerId) target.setPointerCapture?.(id);
      }
    } else if (scaleRef.current > 1) {
      target.setPointerCapture?.(e.pointerId);
    }
    if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      lastPinchDist.current = Math.hypot(b.x - a.x, b.y - a.y);
      lastPinchMid.current = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    }
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent): void => {
      const rec = pointers.current.get(e.pointerId);
      if (!rec) return;
      const rect = (e.currentTarget as Element).getBoundingClientRect();
      const nx = e.clientX - rect.left;
      const ny = e.clientY - rect.top;

      if (pointers.current.size === 1) {
        // Single pointer → pan (only meaningful while zoomed in).
        const dx = nx - rec.x;
        const dy = ny - rec.y;
        rec.x = nx;
        rec.y = ny;
        setState((prev) => {
          if (prev.scale <= 1) return prev;
          const c = clamp(prev.scale, prev.tx + dx, prev.ty + dy);
          return { ...prev, tx: c.tx, ty: c.ty };
        });
        return;
      }

      // Two pointers → pinch. Update this pointer first so the new distance /
      // midpoint reflect both fingers' current positions.
      rec.x = nx;
      rec.y = ny;
      if (pointers.current.size === 2) {
        const [a, b] = Array.from(pointers.current.values());
        const dist = Math.hypot(b.x - a.x, b.y - a.y);
        const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        if (lastPinchDist.current && lastPinchDist.current > 0) {
          const ratio = dist / lastPinchDist.current;
          setState((prev) => {
            const target = Math.min(
              MAX_SCALE,
              Math.max(MIN_SCALE, prev.scale * ratio)
            );
            if (target === prev.scale) return prev;
            const worldX = (mid.x - prev.tx) / prev.scale;
            const worldY = (mid.y - prev.ty) / prev.scale;
            // Also fold in any midpoint translation since last frame — that's
            // the "two-finger drag while pinching" component of the gesture.
            const drift = lastPinchMid.current
              ? { x: mid.x - lastPinchMid.current.x, y: mid.y - lastPinchMid.current.y }
              : { x: 0, y: 0 };
            const nextTx = mid.x - worldX * target + drift.x;
            const nextTy = mid.y - worldY * target + drift.y;
            const c = clamp(target, nextTx, nextTy);
            return { scale: target, tx: c.tx, ty: c.ty };
          });
        }
        lastPinchDist.current = dist;
        lastPinchMid.current = mid;
      }
    },
    [clamp]
  );

  const onPointerUp = useCallback((e: React.PointerEvent): void => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) {
      lastPinchDist.current = null;
      lastPinchMid.current = null;
    }
  }, []);

  const onPointerCancel = onPointerUp;

  const onWheel = useCallback(
    (e: React.WheelEvent): void => {
      // Only react when the gesture is intentional. Most touchpads emit
      // small horizontal wheel deltas while the user scrolls the page; we
      // want to act on vertical wheel only.
      if (Math.abs(e.deltaY) < 1) return;
      const rect = (e.currentTarget as Element).getBoundingClientRect();
      const anchor = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      const factor = e.deltaY < 0 ? WHEEL_STEP : 1 / WHEEL_STEP;
      // Use applyZoom to anchor at the pointer.
      setState((prev) => {
        const target = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev.scale * factor));
        if (target === prev.scale) return prev;
        const worldX = (anchor.x - prev.tx) / prev.scale;
        const worldY = (anchor.y - prev.ty) / prev.scale;
        const nextTx = anchor.x - worldX * target;
        const nextTy = anchor.y - worldY * target;
        const c = clamp(target, nextTx, nextTy);
        return { scale: target, tx: c.tx, ty: c.ty };
      });
    },
    [clamp]
  );

  const onDoubleClick = useCallback((): void => {
    // Double-tap / double-click always resets to 1.0 centred. Predictable
    // recovery affordance for guests who get lost while zoomed in.
    setState({ scale: 1, tx: 0, ty: 0 });
  }, []);

  return {
    scale: state.scale,
    tx: state.tx,
    ty: state.ty,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel,
      onWheel,
      onDoubleClick,
    },
    reset,
    zoomBy,
  };
}
